import { useEffect, useState } from 'react'
import { onValue, ref } from 'firebase/database'

import { db } from '../lib/firebase.js'
import CLUB from '../config/club.js'
import { IS_TEST_SITE } from '../config/siteMode.js'

const PIN_KEY = 'admin_pin'
const ADMIN_KEY = 'adminAuthenticated' // set on successful PIN — TrialGate reads this to suppress itself + banner
const TRIAL_KEY = 'trialAuthenticated' // set by TrialGate on trial-code success — lets trial users skip the PIN
// TEST site requires its OWN pin (1969), NOT the club pin (1914). Club/live keep
// CLUB.defaultPin. IS_TEST_SITE is true only on pbcc-tourney-test.
const DEFAULT_PIN = IS_TEST_SITE ? '1969' : (CLUB.defaultPin || '1914')

// One-time: now that the TEST pin is 1969, invalidate any admin session that was
// unlocked under the old club pin (1914) so ONLY 1969 grants access from here on.
// Runs once per browser (guarded by the epoch marker); club is untouched.
if (IS_TEST_SITE) {
  try {
    if (localStorage.getItem('pinEpoch') !== 'test-1969') {
      localStorage.removeItem(ADMIN_KEY)
      sessionStorage.removeItem(ADMIN_KEY)
      localStorage.setItem('pinEpoch', 'test-1969')
    }
  } catch {}
} else {
  // LIVE: the admin PIN was rotated. Invalidate any admin session that was
  // unlocked under the OLD pin so ONLY the new pin grants access from here on.
  // Runs once per browser (guarded by the epoch marker); test is untouched.
  try {
    if (localStorage.getItem('pinEpoch') !== 'live-rotated-1') {
      localStorage.removeItem(ADMIN_KEY)
      sessionStorage.removeItem(ADMIN_KEY)
      localStorage.setItem('pinEpoch', 'live-rotated-1')
    }
  } catch {}
}

// Auth flags persist in localStorage (survive navigation, new tabs, reloads);
// sessionStorage is read as a fallback so an already-open session still counts.
function readAuthFlag(key) {
  try { return localStorage.getItem(key) === 'true' || sessionStorage.getItem(key) === 'true' } catch { return false }
}

export function getStoredPin() {
  // The admin PIN is ALWAYS the config default (CLUB.defaultPin). We no longer
  // honor a localStorage 'admin_pin' override: no UI writes it, and a stale
  // leftover from an old build was SHADOWING the updated PIN — that's why a
  // changed default "wasn't taking". Clear any legacy key and use the default.
  try { if (localStorage.getItem(PIN_KEY)) localStorage.removeItem(PIN_KEY) } catch {}
  return DEFAULT_PIN
}

// Accidental-delete guard: when an item is LOCKED, a destructive action must be
// confirmed with the admin access code (the PIN). Unlocked → always allowed.
// Returns true when the action may proceed.
export function confirmAccessCodeIfLocked(locked) {
  if (!locked) return true
  const entered = window.prompt('This item is locked. Enter the access code to delete:')
  if (entered == null) return false
  if (String(entered).trim() !== String(getStoredPin())) {
    window.alert('Incorrect access code — delete cancelled.')
    return false
  }
  return true
}

export function clearAdminSession() {
  try { localStorage.removeItem(ADMIN_KEY); sessionStorage.removeItem(ADMIN_KEY) } catch {}
}

export default function PinGate({ children }) {
  const [adminUnlocked, setAdminUnlocked] = useState(
    () => readAuthFlag(ADMIN_KEY),
  )
  const [entry, setEntry] = useState('')
  const [error, setError] = useState('')
  // Subscribe so a trial user reaching /admin doesn't see a PIN prompt.
  // undefined = still loading.
  const [trialActive, setTrialActive] = useState(undefined)
  const trialApproved = readAuthFlag(TRIAL_KEY)

  useEffect(() => {
    let off
    try {
      off = onValue(
        ref(db, 'config/trialAccess'),
        (snap) => setTrialActive(snap.val()?.active === true),
        () => setTrialActive(false),
      )
    } catch {
      setTrialActive(false)
    }
    return () => { try { off?.() } catch {} }
  }, [])

  // Don't flash the PIN screen before we know whether the trial is active.
  if (trialActive === undefined) return null

  // Trial-code path: trial is active and the user already entered the trial
  // code → straight into admin, no PIN, no adminAuthenticated flag (so the
  // banner stays visible everywhere else).
  if (trialActive && trialApproved) return children

  // Real-admin path: PIN was already entered this session.
  if (adminUnlocked) return children

  const submit = (e) => {
    e.preventDefault()
    if (entry === getStoredPin()) {
      try { localStorage.setItem(ADMIN_KEY, 'true') } catch {}
      // Notify TrialGate (and anyone else listening) so it re-reads the
      // flag and stops painting the trial banner over admin pages.
      try { window.dispatchEvent(new Event('authchange')) } catch {}
      setAdminUnlocked(true)
      setEntry('')
      setError('')
    } else {
      setError('Incorrect PIN.')
    }
  }

  return (
    <div className="min-h-full bg-green-mint flex items-center justify-center p-8">
      <form onSubmit={submit} className="card w-full max-w-sm">
        <div className="text-center text-5xl mb-2">🔒</div>
        <h1 className="text-2xl font-extrabold text-green-deep text-center">Admin Portal</h1>
        <p className="text-sm text-gray-600 text-center mt-1">Enter your PIN to continue.</p>
        <input
          autoFocus
          type="password"
          inputMode="numeric"
          pattern="\d*"
          className="input mt-5 text-center tracking-[0.5em] text-2xl"
          placeholder="••••"
          value={entry}
          onChange={(e) => setEntry(e.target.value)}
        />
        {error ? <p className="text-red-600 text-sm mt-2 text-center">{error}</p> : null}
        <button type="submit" className="btn-primary w-full mt-4">Unlock</button>
      </form>
    </div>
  )
}
